import {
  ActivitiesEntryTypeEnum,
  ActivityTagKindValue,
  ICategorizedResponse,
  IResult,
} from "redmond";
import { actions, actionTypes } from "../actions";
import { CombinedSearchGroup, TripDestination } from "../types";

export interface IExperiencesSearchState {
  location: IResult | null;
  locationCategories: ICategorizedResponse[];
  locationCategoriesLoading: boolean | null;
  hasLocationAutocompleteError: boolean;
  fromDate: Date | null;
  untilDate: Date | null;
  categoryTagId?: number | null;
  categoryTagKind?: ActivityTagKindValue | null;
  entryType?: ActivitiesEntryTypeEnum;
  entryTypesAll: ActivitiesEntryTypeEnum[];
  lodgingId: string | null;
  staysAdvance: number | null;
  // Trip destinations state
  tripDestinations: TripDestination[];
  tripDestinationsLoading: boolean;
  tripDestinationsError: string | null;
  tripDestinationsLoaded: boolean;
  // Combined autocomplete state
  combinedAutocompleteGroups: CombinedSearchGroup[];
  combinedAutocompleteLoading: boolean;
  hasCombinedAutocompleteError: boolean;
}

export const initialState: IExperiencesSearchState = {
  location: null,
  locationCategories: [],
  locationCategoriesLoading: false,
  hasLocationAutocompleteError: false,
  fromDate: null,
  untilDate: null,
  categoryTagId: null,
  categoryTagKind: null,
  entryTypesAll: [],
  lodgingId: null,
  staysAdvance: null,
  // Trip destinations initial state
  tripDestinations: [],
  tripDestinationsLoading: false,
  tripDestinationsError: null,
  tripDestinationsLoaded: false,
  // Combined autocomplete initial state
  combinedAutocompleteGroups: [],
  combinedAutocompleteLoading: false,
  hasCombinedAutocompleteError: false,
};

export function reducer(
  state: IExperiencesSearchState = initialState,
  action: actions.ExperiencesSearchActions
): IExperiencesSearchState {
  switch (action.type) {
    case actionTypes.SET_LOCATION: {
      const { location } = action;
      return {
        ...state,
        location: location ? { ...location } : null,
        locationCategoriesLoading: false,
        hasLocationAutocompleteError: false,
      };
    }
    case actionTypes.SET_LOCATION_CATEGORIES: {
      const { categories } = action;
      return {
        ...state,
        locationCategories: categories,
        locationCategoriesLoading: false,
      };
    }
    case actionTypes.FETCH_LOCATION_CATEGORIES:
      return {
        ...state,
        locationCategories: [],
        locationCategoriesLoading: true,
      };

    case actionTypes.SET_LOCATION_AUTOCOMPLETE_ERROR: {
      return {
        ...state,
        hasLocationAutocompleteError: action.error,
        locationCategoriesLoading: false,
      };
    }

    case actionTypes.SET_FROM_DATE:
      return { ...state, fromDate: action.date };

    case actionTypes.SET_UNTIL_DATE:
      return {
        ...state,
        untilDate: action.date,
      };

    case actionTypes.SET_CATEGORY_TAG_ID:
      return {
        ...state,
        categoryTagId: action.categoryTagId,
        categoryTagKind: action.categoryTagKind,
      };

    case actionTypes.SET_ENTRY_TYPE:
      // Only append if the entry type differs from the last entry
      return {
        ...state,
        entryType: action.entryType,
        entryTypesAll:
          state.entryTypesAll[state.entryTypesAll.length - 1] ===
          action.entryType
            ? state.entryTypesAll
            : [...state.entryTypesAll, action.entryType],
      };

    case actionTypes.SET_ENTRY_TYPES_ALL:
      return {
        ...state,
        entryTypesAll: action.entryTypesAll,
      };

    case actionTypes.SET_LODGING_ID:
      return {
        ...state,
        lodgingId: action.lodgingId,
      };

    case actionTypes.SET_STAYS_ADVANCE:
      return {
        ...state,
        staysAdvance: action.staysAdvance,
      };

    // Trip destinations action handlers
    case actionTypes.FETCH_TRIP_DESTINATIONS:
      return {
        ...state,
        tripDestinationsLoading: true,
        tripDestinationsError: null,
      };

    case actionTypes.SET_TRIP_DESTINATIONS:
      return {
        ...state,
        tripDestinations: action.destinations,
        tripDestinationsLoading: false,
        tripDestinationsLoaded: true,
        tripDestinationsError: null,
      };

    case actionTypes.SET_TRIP_DESTINATIONS_LOADING:
      return {
        ...state,
        tripDestinationsLoading: action.loading,
      };

    case actionTypes.SET_TRIP_DESTINATIONS_ERROR:
      return {
        ...state,
        tripDestinationsError: action.error,
        tripDestinationsLoading: false,
        tripDestinationsLoaded: true,
      };

    case actionTypes.SET_TRIP_DESTINATIONS_LOADED:
      return {
        ...state,
        tripDestinationsLoaded: action.loaded,
      };

    // Combined autocomplete action handlers
    case actionTypes.FETCH_COMBINED_AUTOCOMPLETE:
      return {
        ...state,
        combinedAutocompleteGroups: [],
        combinedAutocompleteLoading: true,
        hasCombinedAutocompleteError: false,
      };

    case actionTypes.SET_COMBINED_AUTOCOMPLETE_RESULTS:
      return {
        ...state,
        combinedAutocompleteGroups: action.groups,
        combinedAutocompleteLoading: false,
      };

    case actionTypes.SET_COMBINED_AUTOCOMPLETE_ERROR:
      return {
        ...state,
        hasCombinedAutocompleteError: action.error,
        combinedAutocompleteLoading: false,
      };

    case actionTypes.CLEAR_COMBINED_AUTOCOMPLETE:
      return {
        ...state,
        combinedAutocompleteGroups: [],
        combinedAutocompleteLoading: false,
        hasCombinedAutocompleteError: false,
      };

    default:
      return state;
  }
}

export * from "./selectors";
